import React, { useEffect, useState } from 'react';
import { useGameStore } from '../../stores/gameStore';
import { formatTime } from '../../shared/utils';

export function GameHUD() {
  const gs = useGameStore(s => s.gameState);
  const setScreen = useGameStore(s => s.setScreen);
  const client = useGameStore(s => s.client);
  const [showScoreboard, setShowScoreboard] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.code === 'Tab') {
        e.preventDefault();
        setShowScoreboard(true);
      }
      if (e.code === 'Escape') {
        setShowScoreboard(false);
      }
    };
    const upHandler = (e: KeyboardEvent) => {
      if (e.code === 'Tab') {
        setShowScoreboard(false);
      }
    };
    window.addEventListener('keydown', handler);
    window.addEventListener('keyup', upHandler);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('keyup', upHandler);
    };
  }, []);

  if (!gs.running) return null;

  const healthColor = gs.health > 60 ? 'text-cf-green' : gs.health > 30 ? 'text-cf-amber' : 'text-cf-red';

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }}>
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="px-3 py-1 bg-cf-graphite/80 backdrop-blur-sm border border-cf-border rounded-lg">
            <span className="text-[10px] text-cf-muted uppercase tracking-wider">Room</span>
            <span className="ml-2 text-cf-cyan font-mono font-bold text-xs">{gs.roomId || '---'}</span>
          </div>
          <div className="px-3 py-1 bg-cf-graphite/80 backdrop-blur-sm border border-cf-border rounded-lg">
            <span className={`text-xs font-bold ${gs.isHost ? 'text-cf-green' : 'text-cf-blue'}`}>
              {gs.isHost ? 'HOST' : 'CLIENT'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="px-3 py-1 bg-cf-graphite/80 backdrop-blur-sm border border-cf-border rounded-lg">
            <span className="text-[10px] text-cf-muted uppercase tracking-wider">Time</span>
            <span className="ml-2 text-cf-white font-mono font-bold text-sm">
              {formatTime(gs.timeRemaining)}
            </span>
          </div>
          <div className="px-3 py-1 bg-cf-graphite/80 backdrop-blur-sm border border-cf-border rounded-lg">
            <span className="text-cf-muted text-xs">PING</span>
            <span className={`ml-1.5 font-mono text-xs font-bold ${gs.ping < 50 ? 'text-cf-green' : gs.ping < 100 ? 'text-cf-amber' : 'text-cf-red'}`}>
              {gs.ping}ms
            </span>
          </div>
          <div className="px-3 py-1 bg-cf-graphite/80 backdrop-blur-sm border border-cf-border rounded-lg">
            <span className="text-cf-muted text-xs">FPS</span>
            <span className={`ml-1.5 font-mono text-xs font-bold ${gs.fps >= 120 ? 'text-cf-green' : gs.fps >= 60 ? 'text-cf-amber' : 'text-cf-red'}`}>
              {gs.fps}
            </span>
          </div>
        </div>
      </div>

      {/* Crosshair */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className="relative w-6 h-6">
          <div className="absolute top-1/2 left-0 right-0 h-[2px] -translate-y-1/2 bg-cf-cyan/80" style={{ boxShadow: '0 0 4px rgba(0,229,255,0.5)' }} />
          <div className="absolute left-1/2 top-0 bottom-0 w-[2px] -translate-x-1/2 bg-cf-cyan/80" style={{ boxShadow: '0 0 4px rgba(0,229,255,0.5)' }} />
          <div className="absolute top-1/2 left-1/2 w-1 h-1 -translate-x-1/2 -translate-y-1/2 bg-cf-cyan rounded-full" />
        </div>
      </div>

      {/* Health & Ammo */}
      <div className="absolute bottom-6 left-6 flex items-end gap-4">
        <div className="cf-glass p-4 min-w-[200px]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] text-cf-muted uppercase tracking-wider">Health</span>
            <span className="text-[10px] text-cf-muted uppercase tracking-wider">Armor</span>
          </div>
          <div className="flex items-center justify-between mb-3">
            <span className={`text-3xl font-black ${healthColor}`}>{gs.health}</span>
            <span className="text-xl font-bold text-cf-blue">{gs.armor}</span>
          </div>
          <div className="h-1.5 bg-cf-dark rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-200 ${
                gs.health > 60 ? 'bg-cf-green' : gs.health > 30 ? 'bg-cf-amber' : 'bg-cf-red'
              }`}
              style={{ width: `${gs.health}%` }}
            />
          </div>
          {gs.armor > 0 && (
            <div className="h-1 bg-cf-dark rounded-full overflow-hidden mt-1">
              <div className="h-full bg-cf-blue rounded-full" style={{ width: `${gs.armor}%` }} />
            </div>
          )}
        </div>
      </div>

      <div className="absolute bottom-6 right-6 flex items-end gap-4">
        <div className="cf-glass p-4 min-w-[180px]">
          <div className="text-[10px] text-cf-muted uppercase tracking-wider mb-1">
            {gs.currentWeapon}
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-black text-cf-white">{gs.ammo}</span>
            <span className="text-lg text-cf-muted">/</span>
            <span className="text-lg text-cf-muted">{gs.reserveAmmo}</span>
          </div>
        </div>

        <div className="cf-glass p-4 text-right">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-xl font-black text-cf-green">{gs.kills}</div>
              <div className="text-[10px] text-cf-muted uppercase">Kills</div>
            </div>
            <div>
              <div className="text-xl font-black text-cf-red">{gs.deaths}</div>
              <div className="text-[10px] text-cf-muted uppercase">Deaths</div>
            </div>
            <div>
              <div className="text-xl font-black text-cf-blue">{gs.assists}</div>
              <div className="text-[10px] text-cf-muted uppercase">Assists</div>
            </div>
          </div>
        </div>
      </div>

      {/* Kill Feed */}
      <div className="absolute top-16 right-6 space-y-1.5 max-w-xs">
        {gs.events.slice(-5).reverse().map((event) => (
          <div
            key={event.id}
            className="px-3 py-1.5 bg-cf-graphite/80 backdrop-blur-sm border border-cf-border/50 rounded-lg text-xs text-cf-text animate-slide-up"
          >
            {event.text}
          </div>
        ))}
      </div>

      {/* Scoreboard overlay */}
      {showScoreboard && (
        <div className="absolute inset-0 bg-cf-black/60 backdrop-blur-sm flex items-center justify-center pointer-events-auto">
          <div className="cf-panel p-6 w-full max-w-2xl animate-scale-in">
            <h3 className="text-lg font-bold text-cf-white mb-4">SCOREBOARD</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-cf-muted text-xs uppercase">
                  <th className="text-left py-2">Player</th>
                  <th className="text-center py-2">K</th>
                  <th className="text-center py-2">D</th>
                  <th className="text-center py-2">A</th>
                  <th className="text-center py-2">Score</th>
                </tr>
              </thead>
              <tbody>
                {gs.players.map((p) => (
                  <tr
                    key={p.id}
                    className={`border-t border-cf-border/30 ${p.id === gs.playerId ? 'bg-cf-cyan/5' : ''}`}
                  >
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${p.alive ? 'bg-cf-green' : 'bg-cf-red'}`} />
                        <span className={`font-medium ${p.id === gs.playerId ? 'text-cf-cyan' : 'text-cf-text'}`}>
                          {p.name}
                        </span>
                      </div>
                    </td>
                    <td className="text-center text-cf-green font-mono">{p.kills}</td>
                    <td className="text-center text-cf-red font-mono">{p.deaths}</td>
                    <td className="text-center text-cf-blue font-mono">{p.assists}</td>
                    <td className="text-center text-cf-white font-mono font-bold">
                      {p.kills * 100 + p.assists * 25}
                    </td>
                  </tr>
                ))}
                {gs.players.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-cf-muted">
                      No players connected
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Click to play overlay when pointer not locked */}
      <div
        id="click-overlay"
        className="absolute inset-0 bg-cf-black/40 backdrop-blur-[2px] flex items-center justify-center pointer-events-auto"
        style={{ display: 'none' }}
        onClick={() => {
          document.querySelector('#game-canvas')?.requestPointerLock();
        }}
      >
        <div className="text-center">
          <div className="text-2xl font-bold text-cf-white mb-2">CLICK TO PLAY</div>
          <div className="text-cf-muted text-sm">ESC to pause</div>
        </div>
      </div>
    </div>
  );
}
